import {LABELS} from '@github-ui/issues-shared/labels'
import {useParams} from '@github-ui/react-router'
import {RepoIssuesPageLayout} from '@github-ui/repo-issues-page-layout/RepoIssuesPageLayout'
import {useRepoIssuesSidebarLayoutEnabled} from '@github-ui/repo-issues-page-layout/useSidebarLayoutEnabled'
import type {UserShortcut} from '@github-ui/saved-views'
import {RepositoryViewNewContent} from '@github-ui/saved-views/components/new/RepositoryViewNewContent'
import {ViewsBackLink} from '@github-ui/saved-views/components/ViewsBackLink'
import {useConfirmDiscardChanges} from '@github-ui/saved-views/hooks/use-confirm-discard-changes'
import {useSavedViewEditor} from '@github-ui/saved-views/hooks/use-saved-view-editor'
import {useNavigate} from '@github-ui/use-navigate'
import {useCallback, useEffect} from 'react'

import {renderIssuesSidebar} from '../../components/sidebar/render-issues-sidebar'
import {SavedViewIssueList} from '../../components/views/SavedViewIssueList'
import {REPOSITORY_VIEW} from '../../constants/view-constants'
import {IssueViewsPage, RepositorySavedViewPage} from '../../lazy-routes'
import {preloadWhenIdle} from '../../utils/preload-when-idle'
import {AnalyticsWrapper} from '../AnalyticsWrapper'

const INITIAL_SHORTCUT: UserShortcut = {
  id: '',
  name: '',
  searchType: 'issues',
  query: REPOSITORY_VIEW.query,
  description: '',
  icon: 'bookmark',
  color: 'gray',
}

export function RepositoryViewNewPage() {
  const {owner = '', repo = ''} = useParams<{owner: string; repo: string}>()
  const navigate = useNavigate()
  const editor = useSavedViewEditor({initialShortcut: INITIAL_SHORTCUT})
  const {isDirty} = editor
  const {confirmDiscardIfDirty} = useConfirmDiscardChanges({isDirty})

  useEffect(() => {
    return preloadWhenIdle(() => {
      IssueViewsPage.preload()
      RepositorySavedViewPage.preload()
    })
  }, [])

  const sidebarLayoutEnabled = useRepoIssuesSidebarLayoutEnabled()

  const viewsPath = `/${owner}/${repo}/issues/views`

  const handleBackClick = useCallback(
    async (e: React.MouseEvent<HTMLAnchorElement>) => {
      if (e.defaultPrevented || e.button !== 0) return
      // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return
      if (!isDirty) return

      e.preventDefault()
      if (await confirmDiscardIfDirty()) {
        navigate(viewsPath)
      }
    },
    [isDirty, confirmDiscardIfDirty, navigate, viewsPath],
  )

  const contextArea = sidebarLayoutEnabled ? (
    <ViewsBackLink viewsPath={viewsPath} onClick={handleBackClick} />
  ) : undefined

  return (
    <AnalyticsWrapper category="Repository View New">
      <RepoIssuesPageLayout
        dialogTitle={LABELS.viewsTitle}
        title={sidebarLayoutEnabled ? LABELS.views.createLink : undefined}
        contextArea={contextArea}
        renderSidebar={renderIssuesSidebar}
      >
        <RepositoryViewNewContent
          editor={editor}
          confirmDiscardIfDirty={confirmDiscardIfDirty}
          onBackLinkClick={handleBackClick}
          IssuesRenderer={SavedViewIssueList}
        />
      </RepoIssuesPageLayout>
    </AnalyticsWrapper>
  )
}
